import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Label } from "@progress/kendo-react-labels";
import { DocumentsUpload } from "../../pages/Postings/DcoumentsUpload";
import { onFileUpload } from "services/fileDataService";
import SearchComboBox from "components/common/SearchComboBox";
import CONSTANT from "CONSTANT/Matter/ComboxType";
import MattersCombobox from "pages/Postings/MattersComboBox";
import { useUploadForm } from "components/fileUploadHook";
import {
  validateCounselFee,
  validateAddReference,
  validateDate,
  validateDetails,
  validatePayee,
  validatePayer,
  ValidateVATAmount,
  ValidateNetAmount,
  isBatchPostingRepeatKey,
  blurThenRefocusForValueSync,
} from "../BatchPosting/InputComponents";
import { FileDocumentTypes } from "components/Enums/FileDocumentTypes.ts";
import { NominalSubtypes } from "components/Enums/Nominals.ts";
import Modal from "pages/Settings/Modal";
import { useNominalsBySubType } from "services/nominalsService";
import { getMatterDefaults, searchPostings } from "api";
import { useActiveVats, useVats, resolveVatCode } from "services/vatService";
import { useActiveFeeEarner } from "services/userService";
import {
  getAccountSettingValue,
  AccountSettingKeys,
} from "services/accountSettingsService";
import { useCompany } from "services/companyService";
import { useActiveCurrencies } from "services/currencyService";

import { formatDateForAPI } from "components/common/formatter";

export const MatterDocPostingTypes = [
  "DUU",
  "DPU",
  "DIN",
  "OCR",
  "CTO",
  "ODR",
  "CCR",
  "CDR",
  "DCR",
  "DDR",
  "OTC",
];

const SupplierDocPostingTypes = ["PIN"];
const NominalDocPostingTypes = ["BP", "BR", "BT"];

const NoVatPostingTypes = [
  "CCR",
  "CDR",
  "OCR",
  "DCR",
  "DDABank",
  "ODR",
  "DDR",
  "CTO",
  "OTC",
  "CTC",
  "BT",
  "OTN",
  "NTO",
  "NTB",
];

export default function BatchPostingForm({
  postingType,
  type,
  onChangePosting,
  openChits,
  setInitialData,
  matterId,
  matterIdTo,
  setMatterIdTo,
  isUpdate,
  list,
  updateDataIndex,
  setList,
  setIsUpdate,
  setRenderList,
  postingCodeEdit,
  renderList,
  onChangematter,
  initialData,
  vatCode,
  setVatCode,
  setUpdateDataIndex,
  changePostingCode,
}) {
  const [visible, setVisible] = useState(false);
  const [isError, setIsError] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [filesData, setFilesData] = useState([]);
  const [resetDocs, setresetDocs] = useState(false);
  const [supplierRef, setSupplierRef] = useState(null);
  const [nominalRef, setNominalRef] = useState(null);
  const [documentUpload, setDocumentsUpload] = useState(false);
  const [postingValueForDoc, setPostValueForDoc] = useState(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const { isSuccess, uploadForm, progress, reset } = useUploadForm();
  const [errorModal, setErrorModal] = useState(false);
  const [duplicateWarningModal, setDuplicateWarningModal] = useState(false);
  const [duplicateWarningDetails, setDuplicateWarningDetails] = useState(null);
  const [formVersion, setFormVersion] = useState(0);
  const initialDataSignatureRef = useRef("");
  const pendingAddRef = useRef({ values: null, listSnapshot: null });

  const { data: activeVats = [] } = useActiveVats();
  const { data: allVats = [] } = useVats();
  const { data: activeFeeEarners = [] } = useActiveFeeEarner();
  const { data: company } = useCompany();
  const { data: activeCurrencies = [] } = useActiveCurrencies();
  const baseCurrency = company?.baseCurrency;
  const clientMultiCurrency = Boolean(
    company?.modules?.ClientMultiCurrency ??
    company?.Modules?.ClientMultiCurrency,
  );
  const { data: officeNominals } = useNominalsBySubType(
    NominalSubtypes.OFFICE_BANK,
    {
      excludeArchived: true,
    },
  );
  const { data: clientNominals } = useNominalsBySubType(
    NominalSubtypes.CLIENT_BANK,
    {
      excludeArchived: true,
    },
  );
  const { data: ddaNominals } = useNominalsBySubType(NominalSubtypes.DDA_BANK, {
    excludeArchived: true,
  });

  const shouldShowFeeEarner = (postingCode = postingValueForDoc?.code) =>
    !["BP", "BR", "BT", "PIN"].includes(postingCode);

  const shouldShowOfficeBank = (postingCode = postingValueForDoc?.code) =>
    ["OCR", "ODR", "CTO", "OTC", "BP", "BR", "DPU"].includes(postingCode);

  const shouldShowClientBank = (postingCode = postingValueForDoc?.code) =>
    ["CCR", "CDR", "CTO", "OTC"].includes(postingCode);

  const shouldShowDDABank = (postingCode = postingValueForDoc?.code) =>
    ["DCR", "DDR"].includes(postingCode);

  const applyMatterDefaults = async (
    matterId,
    currentPostingCode,
    isEditMode = false,
  ) => {
    if (!matterId) return;

    try {
      const response = await getMatterDefaults(matterId);
      const defaults = response?.data?.data;
      if (!defaults) return;

      const userFields = [
        "date",
        "netAmount",
        "reference",
        "details",
        "payer",
        "payee",
        "isCounselFee",
        "vatAmount",
      ];

      const newInitialData = userFields.reduce(
        (acc, field) => {
          acc[field] =
            field === "date"
              ? initialData[field] || new Date(Date.now())
              : initialData[field];
          return acc;
        },
        { ...initialData },
      );

      const defaultFields = [
        "vatCode",
        "feeEarnerRef",
        "officeBank",
        "clientBank",
        "ddaBank",
      ];

      if (isEditMode) {
        defaultFields.forEach((field) => {
          newInitialData[field] = initialData[field] || null;
        });
      } else {
        defaultFields.forEach((field) => {
          newInitialData[field] = null;
        });
      }

      const matched = {
        vat: activeVats.find((v) => v.vatCodeId === defaults.defaultVatCodeId),
        feeEarner: activeFeeEarners.find(
          (fe) => fe.userRef === defaults.matterFeeEarner,
        ),
        officeBank: officeNominals.find(
          (n) => n.nominalRef === defaults.defaultOfficeBank,
        ),
        clientBank: clientNominals.find(
          (n) => n.nominalRef === defaults.defaultClientBank,
        ),
        ddaBank: ddaNominals.find(
          (n) => n.nominalRef === defaults.defaultDDABank,
        ),
      };

      if (!isEditMode || !initialData.vatCode) {
        newInitialData.vatCode = matched.vat || null;
        setVatCode(matched.vat || null);
      }

      if (shouldShowFeeEarner(currentPostingCode) && matched.feeEarner) {
        if (!isEditMode || !initialData.feeEarnerRef) {
          newInitialData.feeEarnerRef = {
            userRef: defaults.matterFeeEarner,
            display: matched.feeEarner.display,
          };
        }
      }

      if (shouldShowOfficeBank(currentPostingCode) && matched.officeBank) {
        if (!isEditMode || !initialData.officeBank) {
          newInitialData.officeBank = {
            nominalRef: defaults.defaultOfficeBank,
            display: matched.officeBank.display,
          };
        }
      }

      if (shouldShowClientBank(currentPostingCode) && matched.clientBank) {
        if (!isEditMode || !initialData.clientBank) {
          newInitialData.clientBank = {
            nominalRef: defaults.defaultClientBank,
            display: matched.clientBank.display,
          };
        }
      }

      if (shouldShowDDABank(currentPostingCode) && matched.ddaBank) {
        if (!isEditMode || !initialData.ddaBank) {
          newInitialData.ddaBank = {
            nominalRef: defaults.defaultDDABank,
            display: matched.ddaBank.display,
          };
        }
      }

      setInitialData(newInitialData);
    } catch (error) {
      console.error("Error applying matter defaults:", error);
    }
  };

  const handlePostingChange = async (e) => {
    if (!e) return;

    if (filesData[0]) {
      setErrorModal(true);
      return;
    }
    const postingCode = e.value?.code;
    setPostValueForDoc(e.value);
    onChangePosting(e);

    if (matterId) {
      await applyMatterDefaults(matterId.matterId, postingCode);
    }
  };

  const handleMatterChange = async (e) => {
    if (
      MatterDocPostingTypes.includes(postingValueForDoc?.code) &&
      filesData.length
    ) {
      setErrorModal(true);
      return;
    }

    onChangematter(e);

    if (e?.value?.matterId) {
      await applyMatterDefaults(e.value.matterId, postingValueForDoc?.code);
    } else {
      setInitialData({ date: new Date(Date.now()) });
      setVatCode(null);
    }
  };

  const addNew = () => {
    const cleanInitialData = { date: new Date(Date.now()) };
    setInitialData(cleanInitialData);
    setFilesData([]);
    setVatCode(null);
    setValidationAttempted(false);
  };

  const handleClearFile = (event) => {
    setFilesData([]);
  };

  useEffect(() => {
    if (isUpdate) {
      const pt = initialData?.postingType;
      if (pt) {
        setPostValueForDoc({ ...pt });
      } else if (
        initialData != null &&
        Object.prototype.hasOwnProperty.call(initialData, "postingType") &&
        !pt
      ) {
        setPostValueForDoc(null);
        setHasAutoSelected(false);
      } else if (postingCodeEdit) {
        setPostValueForDoc({ ...postingCodeEdit });
      } else {
        setPostValueForDoc(null);
        setHasAutoSelected(false);
      }
      setValidationAttempted(false);
      return;
    }

    if (postingCodeEdit) {
      setPostValueForDoc({ ...postingCodeEdit });
      setValidationAttempted(false);
    } else {
      setPostValueForDoc(null);
      setHasAutoSelected(false);
    }
  }, [isUpdate, initialData?.postingType, postingCodeEdit]);

  useEffect(() => {
    const sig = JSON.stringify(initialData);
    if (initialDataSignatureRef.current === "") {
      initialDataSignatureRef.current = sig;
    } else if (sig === initialDataSignatureRef.current) {
      return;
    } else {
      initialDataSignatureRef.current = sig;
      setFormVersion((v) => v + 1);
    }
    if (Object.hasOwn(initialData ?? {}, "vatCode")) {
      setVatCode(
        allVats.length
          ? resolveVatCode(initialData.vatCode, allVats) ?? initialData.vatCode
          : initialData.vatCode,
      );
    }
    if (Object.hasOwn(initialData ?? {}, "supplierRef")) {
      setSupplierRef(initialData.supplierRef);
    }
    if (Object.hasOwn(initialData ?? {}, "nominalRef")) {
      setNominalRef(initialData.nominalRef);
    }
  }, [initialData, allVats]);

  useEffect(() => {
    const fillMissingBankFields = async () => {
      if (!isUpdate || !matterId?.matterId || !postingCodeEdit?.code) return;

      const postingCode = postingCodeEdit.code;
      const needsOfficeBank =
        shouldShowOfficeBank(postingCode) && !initialData.officeBank;
      const needsClientBank =
        shouldShowClientBank(postingCode) && !initialData.clientBank;
      const needsDDABank =
        shouldShowDDABank(postingCode) && !initialData.ddaBank;
      const needsFeeEarner =
        shouldShowFeeEarner(postingCode) && !initialData.feeEarnerRef;

      if (
        needsOfficeBank ||
        needsClientBank ||
        needsDDABank ||
        needsFeeEarner
      ) {
        await applyMatterDefaults(matterId.matterId, postingCode, true);
      }
    };

    fillMissingBankFields();
  }, [isUpdate, matterId, postingCodeEdit]);

  const getNetAmountLabelSuffix = useCallback(
    (formRenderProps) => {
      if (!clientMultiCurrency || !postingValueForDoc?.code) return "";
      const pc = postingValueForDoc.code;

      let ccy = null;
      if (["CCR", "CDR", "OTC"].includes(pc)) {
        ccy = formRenderProps.valueGetter("clientBank")?.currencyCode;
      } else if (["DCR", "DDR"].includes(pc)) {
        ccy = formRenderProps.valueGetter("ddaBank")?.currencyCode;
      } else if (pc === "CTC") {
        ccy = formRenderProps.valueGetter("clientBankFrom")?.currencyCode;
      }
      if (!ccy || ccy === baseCurrency) return "";
      const cur = activeCurrencies.find((x) => x.currencyCode === ccy);
      const sym = cur?.symbol || ccy;
      return ` ${sym}`;
    },
    [clientMultiCurrency, postingValueForDoc, baseCurrency, activeCurrencies],
  );

  const handleFormSubmit = async (values, e) => {
    let code = postingValueForDoc.postingType;
    values.postingType = postingValueForDoc;
    values.documentFiles = filesData;
    values.matterID = matterId;
    values.matterIDTo = matterIdTo;
    values.vatCode = vatCode;

    const postingCodeForSubmit = postingValueForDoc?.code;
    if (!shouldShowFeeEarner(postingCodeForSubmit)) {
      values.feeEarnerRef = null;
    }

    if (
      code?.code === "BT" &&
      values?.officeBank?.nominalId === values?.officeBankTo?.nominalId
    ) {
      setIsError(true);
      return;
    }

    const postingCode = postingValueForDoc?.code;

    if (!["PIN", "BP", "BR", "BT"].includes(postingCode) && !matterId) {
      setValidationAttempted(true);
      return;
    }

    if (["DIN", "PIN"].includes(postingCode) && !values.supplierRef) {
      setValidationAttempted(true);
      return;
    }

    if (
      ["PIN", "BP", "BR", "OTN", "NTO", "NTB"].includes(postingCode) &&
      !values.nominalRef
    ) {
      setValidationAttempted(true);
      return;
    }

    if (["PIN", "BP", "BR"].includes(postingCode) && !values.branchId) {
      setValidationAttempted(true);
      return;
    }

    if (postingCode === "CTC") {
      if (
        !matterId ||
        !matterIdTo ||
        !values.clientBankFrom ||
        !values.clientBankTo
      ) {
        setValidationAttempted(true);
        return;
      }
    }

    if (clientMultiCurrency && postingCode === "CTC") {
      const fromC = values.clientBankFrom?.currencyCode;
      const toC = values.clientBankTo?.currencyCode;
      if (fromC && toC && fromC !== toC) {
        setValidationAttempted(true);
        return;
      }
    }

    if (
      clientMultiCurrency &&
      postingCode === "CTO" &&
      values.clientBank?.currencyCode &&
      values.clientBank.currencyCode !== baseCurrency
    ) {
      setValidationAttempted(true);
      return;
    }

    if (shouldShowClientBank(postingCode) && !values.clientBank) {
      setValidationAttempted(true);
      return;
    }

    if (shouldShowOfficeBank(postingCode) && !values.officeBank) {
      setValidationAttempted(true);
      return;
    }

    if (shouldShowDDABank(postingCode) && !values.ddaBank) {
      setValidationAttempted(true);
      return;
    }

    if (shouldShowFeeEarner(postingCode) && !values.feeEarnerRef) {
      setValidationAttempted(true);
      return;
    }

    if (NoVatPostingTypes.includes(postingCode)) {
      values.vatAmount = null;
      values.vatCode = null;
    } else if (values.vatAmount === undefined) {
      values.vatAmount = (values?.vatCode?.rate / 100) * values?.netAmount;
      if (values.vatAmount === null || isNaN(values.vatAmount)) {
        values.vatAmount = 0.0;
      }
    }

    if (!isUpdate && values.date && values.reference) {
      try {
        const disableSamePostingCheck = await getAccountSettingValue(
          AccountSettingKeys.DISABLE_SAME_POSTING_CHECK,
          false,
        );

        if (!disableSamePostingCheck) {
          const dateStr =
            values.date instanceof Date
              ? formatDateForAPI(values.date)
              : values.date;

          const response = await searchPostings(dateStr, values.reference, 1);
          const existingPostings = response?.data?.data || [];

          if (existingPostings.length > 0) {
            setDuplicateWarningDetails({
              dateStr,
              reference: values.reference,
              transactionNo: existingPostings[0].transactionNo,
            });
            pendingAddRef.current = {
              values: { ...values },
              listSnapshot: [...list],
            };
            setDuplicateWarningModal(true);
            return;
          }
        }
      } catch (error) {
        console.error("Error checking for duplicate postings:", error);
      }
    }

    if (isUpdate && list) {
      const listClone = [...list];
      listClone[updateDataIndex] = { ...values };
      setList([...listClone]);
      setIsUpdate(false);
    } else {
      if (!list) return;
      setList([...list, values]);
    }

    setIsError(false);
    setValidationAttempted(false);
    addNew();
    document.getElementById("reset-button").click();
    onChangematter({});
    setMatterIdTo();
    setVatCode(null);
    setNominalRef(null);
  };

  const handleUpload = async (e) => {
    let data = onFileUpload(e);
    setFilesData([...filesData, data]);

    const parameters = {
      documentType: FileDocumentTypes.Posting,
      matterId: MatterDocPostingTypes.includes(postingValueForDoc?.code)
        ? matterId?.matterId
        : undefined,
      supplierRef: SupplierDocPostingTypes.includes(postingValueForDoc?.code)
        ? supplierRef?.supplierRef
        : undefined,
      nominalRef: NominalDocPostingTypes.includes(postingValueForDoc?.code)
        ? nominalRef?.nominalRef
        : undefined,
    };
    const dataChoosen = await uploadForm(data, e.target.files[0], parameters);
    data.fileID = dataChoosen.data.data.fileID;
  };
  const onFileRemove = (fileId) => {
    setFilesData((prev) => prev.filter((file) => file.fileID !== fileId));
  };

  const requiredValidator = (value) => (value ? "" : "This field is required.");

  const getPostingCompatibility = () => {
    if (postingValueForDoc === null) return false;
    if (
      MatterDocPostingTypes.includes(postingValueForDoc?.code) &&
      matterId !== null
    )
      return true;
    if (
      SupplierDocPostingTypes.includes(postingValueForDoc?.code) &&
      supplierRef !== null
    )
      return true;
    if (
      NominalDocPostingTypes.includes(postingValueForDoc?.code) &&
      nominalRef !== null
    )
      return true;
    return false;
  };

  const notAllowedChange = () =>
    MatterDocPostingTypes.includes(postingValueForDoc?.code) &&
    filesData.length;

  const handleDuplicateWarningOk = () => {
    const { values: pendingValues, listSnapshot } = pendingAddRef.current;
    if (!pendingValues || !listSnapshot) {
      setDuplicateWarningModal(false);
      return;
    }
    setList([...listSnapshot, pendingValues]);
    setIsError(false);
    setValidationAttempted(false);
    addNew();
    document.getElementById("reset-button")?.click();
    onChangematter({});
    setMatterIdTo();
    setVatCode(null);
    setNominalRef(null);
    pendingAddRef.current = { values: null, listSnapshot: null };
    setDuplicateWarningDetails(null);
    setDuplicateWarningModal(false);
  };

  const handleDuplicateWarningCancel = () => {
    pendingAddRef.current = { values: null, listSnapshot: null };
    setDuplicateWarningDetails(null);
    setDuplicateWarningModal(false);
  };

  const handleGlobalKeyDown = async (e, formRenderProps) => {
    if (isBatchPostingRepeatKey(e) && list && list.length > 0) {
      const lastPosting = list[list.length - 1];
      const activeElement = document.activeElement;

      if (!lastPosting) return;

      e.preventDefault();
      e.stopPropagation();

      const isComboBox =
        activeElement?.closest(".k-combobox") !== null ||
        activeElement?.closest(".k-dropdown") !== null ||
        activeElement?.role === "combobox";

      let fieldName = null;

      if (isComboBox) {
        const comboBoxContainer = activeElement?.closest(
          ".k-combobox, .k-dropdown",
        );
        const parentDiv = comboBoxContainer?.closest(".mb-2");
        const label = parentDiv?.querySelector(".label")?.textContent?.trim();

        if (
          label === "Matter" ||
          label === "From Matter" ||
          label === "To Matter"
        ) {
          if (lastPosting.matterID) {
            if (label === "To Matter") {
              setMatterIdTo(lastPosting.matterIDTo || lastPosting.matterID);
            } else {
              onChangematter({ value: lastPosting.matterID });
            }
            return;
          }
        }

        const inputElement = comboBoxContainer?.querySelector("input[name]");
        fieldName = inputElement?.name;

        if (!fieldName) {
          const labelToFieldMap = {
            "Posting Type": "postingType",
            Supplier: "supplierRef",
            Nominal: "nominalRef",
            "Fee Earner": "feeEarnerRef",
            "Office Bank": "officeBank",
            "Client Bank": "clientBank",
            "From Client Bank": "clientBankFrom",
            "To Client Bank": "clientBankTo",
            "From Bank": "officeBank",
            "To Bank": "officeBankTo",
            "DDA Bank": "ddaBank",
            "VAT Code": "vatCode",
            Branch: "branchId",
          };

          fieldName = labelToFieldMap[label];
        }
      } else {
        fieldName = activeElement?.name || activeElement?.id;
      }

      const fieldMapping = {
        postingType: async () => {
          if (!lastPosting.postingType) return;
          if (filesData.length > 0) {
            setErrorModal(true);
            return;
          }
          setPostValueForDoc(lastPosting.postingType);
          formRenderProps.onChange("postingType", {
            value: lastPosting.postingType,
          });
          onChangePosting({ value: lastPosting.postingType });
          if (matterId?.matterId) {
            await applyMatterDefaults(
              matterId.matterId,
              lastPosting.postingType?.code,
            );
          }
        },
        supplierRef: () => {
          if (lastPosting.supplierRef) {
            formRenderProps.onChange("supplierRef", {
              value: lastPosting.supplierRef,
            });
            setSupplierRef(lastPosting.supplierRef);
          }
        },
        nominalRef: () => {
          if (lastPosting.nominalRef) {
            formRenderProps.onChange("nominalRef", {
              value: lastPosting.nominalRef,
            });
            setNominalRef(lastPosting.nominalRef);
          }
        },
        feeEarnerRef: () => {
          if (!shouldShowFeeEarner(postingValueForDoc?.code)) return;
          if (lastPosting.feeEarnerRef) {
            formRenderProps.onChange("feeEarnerRef", {
              value: lastPosting.feeEarnerRef,
            });
          }
        },
        officeBank: () => {
          if (lastPosting.officeBank) {
            formRenderProps.onChange("officeBank", {
              value: lastPosting.officeBank,
            });
          }
        },
        clientBank: () => {
          if (lastPosting.clientBank) {
            formRenderProps.onChange("clientBank", {
              value: lastPosting.clientBank,
            });
          }
        },
        clientBankFrom: () => {
          if (lastPosting.clientBankFrom) {
            formRenderProps.onChange("clientBankFrom", {
              value: lastPosting.clientBankFrom,
            });
          }
        },
        clientBankTo: () => {
          if (lastPosting.clientBankTo) {
            formRenderProps.onChange("clientBankTo", {
              value: lastPosting.clientBankTo,
            });
          }
        },
        ddaBank: () => {
          if (lastPosting.ddaBank) {
            formRenderProps.onChange("ddaBank", { value: lastPosting.ddaBank });
          }
        },
        vatCode: () => {
          if (lastPosting.vatCode) {
            setVatCode(lastPosting.vatCode);
            formRenderProps.onChange("vatCode", { value: lastPosting.vatCode });

            const currentNetAmount = formRenderProps.valueGetter("netAmount");
            if (currentNetAmount) {
              const vatRate = lastPosting.vatCode.rate || 0;
              const calculatedVat = currentNetAmount * (vatRate / 100);
              formRenderProps.onChange("vatAmount", { value: calculatedVat });
            }
          }
        },
        branchId: () => {
          if (lastPosting.branchId) {
            formRenderProps.onChange("branchId", {
              value: lastPosting.branchId,
            });
          }
        },
        netAmount: () => {
          if (
            lastPosting.netAmount !== undefined &&
            lastPosting.netAmount !== null
          ) {
            formRenderProps.onChange("netAmount", {
              value: lastPosting.netAmount,
            });
            const vatRate = lastPosting.vatCode?.rate || 0;
            const calculatedVat = lastPosting.netAmount * (vatRate / 100);
            formRenderProps.onChange("vatAmount", { value: calculatedVat });
            blurThenRefocusForValueSync();
          }
        },
        vatAmount: () => {
          if (
            lastPosting.vatAmount !== undefined &&
            lastPosting.vatAmount !== null
          ) {
            formRenderProps.onChange("vatAmount", {
              value: lastPosting.vatAmount,
            });
            blurThenRefocusForValueSync();
          }
        },
      };

      if (fieldName && fieldMapping[fieldName]) {
        await fieldMapping[fieldName]();
      }
    }
  };

  return (
    <>
      <div
        className="bgForm h-100 ms-2 pt-4 d-flex justify-content-center align-item-center"
        style={{ minWidth: "350px", maxWidth: "350px", overflow: "auto" }}
      >
        <Form
          ignoreModified={true}
          onSubmit={handleFormSubmit}
          initialValues={initialData}
          key={formVersion}
          render={(formRenderProps) => {
            return (
              <div onKeyDown={(e) => handleGlobalKeyDown(e, formRenderProps)}>
                <FormElement noValidate>
                  <fieldset>
                    <div className="mb-2">
                      <div className="mb-2">
                        <SearchComboBox
                          onChangeFunction={(e) => handlePostingChange(e)}
                          customCombo
                          value={postingValueForDoc}
                          name="postingType"
                          cbType={CONSTANT.POSTING_TYPE}
                          width="auto"
                          boxShadow={true}
                          postingType={postingType}
                          error={validationAttempted && !postingValueForDoc}
                          autoSelectFirst={true}
                          hasAutoSelected={hasAutoSelected}
                          setHasAutoSelected={setHasAutoSelected}
                          deferChangeUntilClose={true}
                        />
                      </div>
                    </div>

                    {!renderList?.some((el) =>
                      ["PIN", "BP", "BR", "BT", "CTC"].includes(el),
                    ) && (
                        <div className="mb-2">
                          <Label className="label">Matter</Label>
                          <div>
                            <MattersCombobox
                              style={{
                                boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
                              }}
                              filesData={filesData}
                              onChangematter={(e) => {
                                handleMatterChange(e);
                              }}
                              name="matterID"
                              value={matterId}
                              setErrorModal={setErrorModal}
                              notAllowedChange={notAllowedChange}
                              width="100%"
                              error={validationAttempted && !matterId}
                              openMattersOnly={true}
                              deferChangeUntilClose={true}
                            />
                          </div>
                        </div>
                      )}

                    {renderList?.some((el) => ["CTC"].includes(el)) && (
                      <div className="mb-2">
                        <Label className="label">From Matter</Label>
                        <div>
                          <MattersCombobox
                            style={{
                              boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
                            }}
                            filesData={filesData}
                            onChangematter={(e) => {
                              handleMatterChange(e);
                            }}
                            name="matterIDFrom"
                            value={matterId}
                            setErrorModal={setErrorModal}
                            notAllowedChange={notAllowedChange}
                            width="100%"
                            error={validationAttempted && !matterId}
                            openMattersOnly={true}
                            deferChangeUntilClose={true}
                          />
                        </div>
                      </div>
                    )}

                    {renderList?.some((el) => ["CTC"].includes(el)) && (
                      <div className="mb-2">
                        <SearchComboBox
                          name="clientBankFrom"
                          cbType={CONSTANT.CB}
                          customLabel={"From Client Bank"}
                          width="auto"
                          boxShadow={true}
                          error={
                            validationAttempted &&
                            !formRenderProps.valueGetter("clientBankFrom")
                          }
                          onAfterFieldChange={(e) => {
                            const to =
                              formRenderProps.valueGetter("clientBankTo");
                            if (!e.value) {
                              if (to) {
                                formRenderProps.onChange("clientBankTo", {
                                  value: null,
                                });
                              }
                              return;
                            }
                            if (
                              to &&
                              e.value?.currencyCode &&
                              to.currencyCode !== e.value.currencyCode
                            ) {
                              formRenderProps.onChange("clientBankTo", {
                                value: null,
                              });
                            }
                          }}
                        />
                      </div>
                    )}

                    {renderList?.some((el) => ["CTC"].includes(el)) && (
                      <div className="mb-2">
                        <Label className="label">To Matter</Label>
                        <div>
                          <MattersCombobox
                            style={{
                              boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
                            }}
                            filesData={filesData}
                            onChangematter={(e) => {
                              setMatterIdTo(e.value);
                            }}
                            name="matterIDTo"
                            value={matterIdTo}
                            setErrorModal={setErrorModal}
                            notAllowedChange={notAllowedChange}
                            width="100%"
                            error={validationAttempted && !matterIdTo}
                            openMattersOnly={true}
                            deferChangeUntilClose={true}
                          />
                        </div>
                      </div>
                    )}

                    {renderList?.some((el) => ["CTC"].includes(el)) && (
                      <div className="mb-2">
                        <SearchComboBox
                          name="clientBankTo"
                          cbType={CONSTANT.CB}
                          customLabel={"To Client Bank"}
                          width="auto"
                          boxShadow={true}
                          error={
                            validationAttempted &&
                            !formRenderProps.valueGetter("clientBankTo")
                          }
                          restrictClientBankCurrencyCode={
                            clientMultiCurrency
                              ? formRenderProps.valueGetter("clientBankFrom")
                                ?.currencyCode
                              : undefined
                          }
                        />
                      </div>
                    )}

                    {renderList?.some((el) => ["DIN", "PIN"].includes(el)) && (
                      <div className="mb-2">
                        <SearchComboBox
                          name="supplierRef"
                          cbType={CONSTANT.SR}
                          postingType={postingValueForDoc?.code}
                          width="auto"
                          boxShadow={true}
                          error={
                            validationAttempted &&
                            !formRenderProps.valueGetter("supplierRef")
                          }
                        />
                      </div>
                    )}

                    <div className="d-flex mb-2">
                      <div style={{ width: "150px", marginRight: "15px" }}>
                        <Field
                          name="date"
                          component={validateDate}
                          list={list}
                        />
                      </div>
                      <div style={{ width: "156px" }}>
                        <Field
                          validator={requiredValidator}
                          name="reference"
                          component={validateAddReference}
                          validationAttempted={validationAttempted}
                          list={list}
                        />
                      </div>
                    </div>

                    <div
                      className={`d-flex  ${renderList[0] === "CTO"
                          ? " flex-column"
                          : " flex-column-reverse"
                        }`}
                    >
                      {renderList?.some((el) =>
                        ["CCR", "CDR", "CTO", "OTC"].includes(el),
                      ) && (
                          <div className="mb-2">
                            <SearchComboBox
                              name="clientBank"
                              cbType={CONSTANT.CB}
                              width="auto"
                              boxShadow={true}
                              error={
                                validationAttempted &&
                                !formRenderProps.valueGetter("clientBank")
                              }
                              restrictClientBankCurrencyCode={
                                clientMultiCurrency &&
                                  postingValueForDoc?.code === "CTO"
                                  ? baseCurrency
                                  : undefined
                              }
                            />
                          </div>
                        )}
                      {renderList?.some((el) =>
                        [
                          "OCR",
                          "ODR",
                          "CTO",
                          "OTC",
                          "BP",
                          "BR",
                          "DPU",
                        ].includes(el),
                      ) && (
                          <div className="mb-2">
                            <SearchComboBox
                              name="officeBank"
                              cbType={CONSTANT.OB}
                              width="auto"
                              boxShadow={true}
                              error={
                                validationAttempted &&
                                !formRenderProps.valueGetter("officeBank")
                              }
                            />
                          </div>
                        )}
                    </div>
                    {renderList?.some((el) =>
                      ["PIN", "BP", "BR", "OTN", "NTO", "NTB"].includes(el),
                    ) && (
                        <div className="mb-2">
                          <SearchComboBox
                            name="nominalRef"
                            cbType={CONSTANT.NR}
                            width="auto"
                            boxShadow={true}
                            error={
                              validationAttempted &&
                              !formRenderProps.valueGetter("nominalRef")
                            }
                          />
                        </div>
                      )}

                    {renderList?.some((el) => ["BT"].includes(el)) && (
                      <div className="mb-2">
                        <SearchComboBox
                          name="officeBank"
                          cbType={CONSTANT.FBT}
                          width="auto"
                          boxShadow={true}
                          toValue={formRenderProps?.valueGetter("officeBankTo")}
                          error={isError}
                        />
                      </div>
                    )}

                    {renderList?.some((el) => ["BT"].includes(el)) && (
                      <div className="mb-2">
                        <SearchComboBox
                          name="officeBankTo"
                          cbType={CONSTANT.TBT}
                          width="auto"
                          boxShadow={true}
                          fromValue={formRenderProps?.valueGetter(
                            "officeBankFrom",
                          )}
                          error={isError}
                        />
                      </div>
                    )}

                    {renderList?.some((el) => ["DCR", "DDR"].includes(el)) && (
                      <div className="mb-2">
                        <SearchComboBox
                          name="ddaBank"
                          cbType={CONSTANT.DDAB}
                          width="auto"
                          boxShadow={true}
                          error={
                            validationAttempted &&
                            !formRenderProps.valueGetter("ddaBank")
                          }
                        />
                      </div>
                    )}

                    <div className="mb-2">
                      <Field
                        validator={requiredValidator}
                        name="netAmount"
                        component={ValidateNetAmount}
                        formRenderProps={formRenderProps}
                        NetAmount={formRenderProps.valueGetter("netAmount")}
                        VatCode={vatCode}
                        netAmountLabelSuffix={getNetAmountLabelSuffix(
                          formRenderProps,
                        )}
                        validationAttempted={validationAttempted}
                        list={list}
                      />
                    </div>

                    {!renderList?.some((el) =>
                      NoVatPostingTypes.includes(el),
                    ) && (
                        <div className="d-flex mb-2 ">
                          <div style={{ width: "160px", marginRight: "10px" }}>
                            <SearchComboBox
                              name="vatCode"
                              cbType={CONSTANT.AVATS}
                              width="auto"
                              customCombo={true}
                              onChangeFunction={(e) => {
                                const vatRate = e?.value?.rate || 0; // Use 0 if VatCode is not provided or invalid
                                const calculatedVat =
                                  formRenderProps.valueGetter("netAmount") *
                                  (vatRate / 100);
                                formRenderProps.onChange("vatAmount", {
                                  value: !isNaN(calculatedVat)
                                    ? calculatedVat
                                    : 0.0,
                                });
                                setVatCode(e.value);
                              }}
                              value={vatCode}
                              boxShadow={true}
                              required={true}
                              error={validationAttempted && !vatCode}
                              defaultValueSetter={formRenderProps}
                            />
                          </div>
                          <div style={{ width: "156px" }}>
                            <Field
                              name="vatAmount"
                              component={ValidateVATAmount}
                              NetAmount={formRenderProps.valueGetter("netAmount")}
                              VatCode={formRenderProps.valueGetter("vatCode")}
                              list={list}
                            />
                          </div>
                        </div>
                      )}
                    {shouldShowFeeEarner(postingValueForDoc?.code) && (
                      <div className="mb-2">
                        <SearchComboBox
                          name="feeEarnerRef"
                          cbType={CONSTANT.FE}
                          width="auto"
                          boxShadow={true}
                          error={
                            validationAttempted &&
                            !formRenderProps.valueGetter("feeEarnerRef")
                          }
                        />
                      </div>
                    )}

                    {renderList?.some((el) =>
                      ["OCR", "CCR", "DCR", "BR", "BT"].includes(el),
                    ) && (
                        <div className="mb-2">
                          <Field
                            validator={requiredValidator}
                            name="payer"
                            component={validatePayer}
                            validationAttempted={validationAttempted}
                            list={list}
                          />
                        </div>
                      )}

                    {renderList?.some((el) =>
                      ["CDR", "ODR", "DDR", "BP", "DPU"].includes(el),
                    ) && (
                        <div className="mb-2">
                          <Field
                            validator={requiredValidator}
                            name="payee"
                            component={validatePayee}
                            validationAttempted={validationAttempted}
                            list={list}
                          />
                        </div>
                      )}

                    {renderList?.some((el) =>
                      ["PIN", "BP", "BR"].includes(el),
                    ) && (
                        <div className="mb-2">
                          <SearchComboBox
                            name="branchId"
                            cbType={CONSTANT.BRANCHES}
                            width="auto"
                            boxShadow={true}
                            error={
                              validationAttempted &&
                              !formRenderProps.valueGetter("branchId")
                            }
                          />
                        </div>
                      )}
                    <div className="mb-2">
                      <Field
                        validator={requiredValidator}
                        name="details"
                        component={validateDetails}
                        validationAttempted={validationAttempted}
                        list={list}
                      />
                    </div>

                    {renderList?.some((el) => ["DUU", "DIN"].includes(el)) && (
                      <Field
                        name="isCounselFee"
                        component={validateCounselFee}
                      />
                    )}

                    <div
                      className={`d-flex mt-2 justify-content-between align-items-center ${type === "chitPosting" && "justify-content-end"
                        }`}
                    >
                      {type !== "chitPosting" && (
                        <p
                          className="me-3 mt-2"
                          onClick={openChits}
                          type="button"
                          style={{ color: "#6171FF", fontSize: "15px" }}
                          disabled={
                            postingValueForDoc === "" ||
                            postingValueForDoc === null
                          }
                        >
                          <u>Select Chit</u>
                        </p>
                      )}
                      <div className="ms-3">
                        <DocumentsUpload
                          position="static"
                          open={documentUpload}
                          value={postingValueForDoc}
                          type={"All Postings"}
                          disabled={!getPostingCompatibility()}
                          reset={resetDocs}
                          progress={progress}
                          filesData={filesData}
                          onFileRemove={onFileRemove}
                          onError={() => {
                            const newFiles = filesData.splice(
                              filesData.length,
                              1,
                            );
                            setFilesData([...newFiles]);
                          }}
                          onUpload={handleUpload}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="d-none"
                      id="reset-button"
                      onClick={() => {
                        formRenderProps.onFormReset();
                      }}
                    >
                      clc
                    </button>
                    <div className="d-flex mb-2 justify-content-end gap-2">
                      {isUpdate && (
                        <div className="k-form-buttons">
                          <button
                            type="button"
                            className="common-btn border-0"
                            onClick={() => {
                              setIsUpdate(false);
                              setRenderList([]);
                              changePostingCode?.(null);
                              setPostValueForDoc(null);
                              setHasAutoSelected(false);
                              setVisible(false);
                              setUpdateDataIndex(0);
                              onChangematter({});
                              setValidationAttempted(false);
                              setInitialData({ date: new Date(Date.now()) });
                              onChangePosting?.({ value: null });
                            }}
                          >
                            Cancel Update
                          </button>
                        </div>
                      )}
                      <div className="k-form-buttons">
                        <button
                          type="button"
                          className="common-btn border-0"
                          onClick={(e) => {
                            e.preventDefault();
                            setValidationAttempted(true);
                            setTimeout(() => {
                              formRenderProps.onSubmit(e);
                            }, 10);
                          }}
                        >
                          {isUpdate ? "Update" : "Add to batch"}
                        </button>
                      </div>
                    </div>
                  </fieldset>
                </FormElement>
              </div>
            );
          }}
        />
        <Modal
          value={errorModal}
          error={true}
          reset={() => {
            setErrorModal(false);
          }}
          onCancel={() => {
            setErrorModal(false);
          }}
          component={"Posting"}
          display={
            "You have unposted files! By changing posting type you will lose files!"
          }
          clearFile={handleClearFile}
        ></Modal>
        {duplicateWarningModal && (
          <Dialog
            title="Duplicate posting"
            onClose={handleDuplicateWarningCancel}
            width={480}
          >
            <div className="d-flex flex-column align-items-center px-4 py-4 text-center">
              <span className="k-icon k-i-warning fs-1 text-warning mb-3" />
              <p
                className="mb-0 text-secondary text-start"
                style={{ lineHeight: 1.5, maxWidth: "100%" }}
              >
                {duplicateWarningDetails && (
                  <>
                    A posting with the same date{" "}
                    <strong className="text-dark">
                      {duplicateWarningDetails.dateStr}
                    </strong>{" "}
                    and reference{" "}
                    <strong className="text-dark">
                      {duplicateWarningDetails.reference}
                    </strong>{" "}
                    already exists. Transaction No:{" "}
                    <strong className="text-dark">
                      {duplicateWarningDetails.transactionNo}
                    </strong>
                    . Do you want to add anyway?
                  </>
                )}
              </p>
            </div>
            <div className="dialog-footer w-100 d-flex justify-content-end gap-2 px-4">
              <button
                type="button"
                className="common-btn bgBtnSecondary"
                onClick={handleDuplicateWarningCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="k-button editBtn common-btn"
                onClick={handleDuplicateWarningOk}
              >
                OK
              </button>
            </div>
          </Dialog>
        )}
      </div>
    </>
  );
}
